import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePostRoles } from '@dhl/keycloak-user-api'
import { Box, Button, Card, Checkbox, Divider, Grid, List } from '@dhl-ui/components'
import { useCurrentUser, useNotificationMessage } from '~/common/hooks'
import { useKeyCloakRoles, useKeyCloakUser } from '~/modules/adminusers/hooks'
import { convertBusinessUnitToRole, type RoleName } from '~/modules/adminusers/model'

type Props = {
  userId: string
  userRoles: RoleName[]
  userBusinessUnits: string[]
  onBack: () => void
  onRefetch: () => void
}

export const RoleMappingsCard = ({ userId, userRoles, userBusinessUnits, onBack, onRefetch }: Props) => {
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const { roleCategories } = useKeyCloakRoles()
  const { mutateAsync: postUserRoles } = usePostRoles()
  const { id: currentUserId } = useCurrentUser()
  const { userRoles: currentUserRoles } = useKeyCloakUser(currentUserId!)
  const [selectedRoles, setSelectedRoles] = useState<RoleName[]>([])

  useEffect(() => {
    if (userRoles) {
      // When user roles are loaded from the api, set them as default for the selection
      setSelectedRoles(userRoles)
    }
  }, [userRoles])

  const isEqual = (role1: RoleName) => (role2: RoleName) => role1 === role2

  const handleSelectRole = (role: RoleName) => () => {
    const isRoleSelected = selectedRoles.some(isEqual(role))
    const newSelectedRoles = isRoleSelected
      ? selectedRoles.filter((roleName) => roleName !== role)
      : selectedRoles.concat(role)
    setSelectedRoles(newSelectedRoles)
  }
  const handleSelectGroup = (roles: RoleName[], checked: boolean) => () => {
    const editableRolesInCategory = roles.filter(currentUserCanEditRole)
    const newSelectedRoles = selectedRoles
      .filter((role) => !editableRolesInCategory.some(isEqual(role)))
      .concat(checked ? editableRolesInCategory : [])
    setSelectedRoles(newSelectedRoles)
  }

  const handleSave = async () => {
    const keyCloakBusinessUnits = userBusinessUnits.map(convertBusinessUnitToRole)
    // In keycloak business units are stored as roles
    const newKeyCloakRoles = keyCloakBusinessUnits.concat(selectedRoles)
    await postUserRoles(
      {
        p1: userId,
        data: newKeyCloakRoles,
      },
      {
        onSuccess: () => {
          showSuccess(t('admin.adminUsersPage.rolesPage.updateRole'))
        },
        onError: () => {
          showError(t('admin.adminUsersPage.rolesPage.errorMessage'))
        },
      }
    )
    onRefetch()
    onBack()
  }

  if (!currentUserRoles || !userRoles) return null

  const currentUserCanEditRole = (role: RoleName) => {
    return currentUserRoles.some(isEqual(role))
  }

  return (
    <Card data-test="roles-form">
      <Card.Content>
        <List key={`category`}>
          {roleCategories.map(({ name, roles }) => {
            const selectedCategoryRoles = selectedRoles.filter((roleName) => roles.includes(roleName))
            const isIndeterminate = selectedCategoryRoles.length >= 1 && selectedCategoryRoles.length < roles.length
            const checked = roles.length === selectedCategoryRoles.length
            return (
              <Fragment key={`category-${name}`}>
                <Checkbox
                  checked={checked}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectGroup(roles, !checked)}
                  label={name}
                />
                {roles.map((role) => {
                  const isRoleSelected = selectedRoles.some((selectedRole) => selectedRole === role)
                  const canEditRole = currentUserCanEditRole(role)
                  return (
                    <Box key={`role-${role}`} sx={{ pl: 4, display: 'block', mt: 0.5 }}>
                      <Checkbox
                        disabled={!canEditRole}
                        checked={isRoleSelected}
                        onChange={handleSelectRole(role)}
                        label={t(`admin.adminUsersPage.rolesPage.${role}`)}
                      />
                    </Box>
                  )
                })}
                <Divider sx={{ my: 2 }} />
              </Fragment>
            )
          })}
        </List>

        <Grid marginTop={2} display="flex" justifyContent="flex-end" gap={1}>
          <Button data-test="click-cancel" size="medium" variant="outlined" color="primary" onClick={onBack}>
            {t('admin.adminUsersPage.userForm.cancelButtonTitle')}
          </Button>
          <Button data-test="click-save" size="medium" variant="contained" color="primary" onClick={handleSave}>
            {t('admin.adminUsersPage.userForm.saveButtonTitle')}
          </Button>
        </Grid>
      </Card.Content>
    </Card>
  )
}
